import { TAGSvgIcon, dataLayerConfig, useAppContext } from '@aspendental/shared-components-web';
import { useFacilityContext } from '@aspendental/shared-components-web/lib/context/FacilityContextProvider.ctx';
import { getBffFacilitiesByAddress } from '@aspendental/shared-components-web/lib/services';
import { Box, IconButton, OutlinedInput } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { dialogStyles } from '../SelectLocationDialog.styles';
import BffLocationsList from './BffLocationsList';
import RestApiLocationsList from './RestApiLocationsList';
import { HEART_LOCATION_STORAGE_KEY, treatmentCodeToReasonCode } from '@src/constants';
import { BffBrandType, RequestStatus } from '@src/lib/types';
import { useAppStore, useSchedulingStore } from '@src/store';
import { pushToDataLayer } from '@src/utils/pushToDataLayer';
import { BffFacility, CenterLocation } from 'types/location';
export interface ILocationSearch {
  locations: CenterLocation[];
  handleClose: () => void;
  preSelectedLocation?: CenterLocation;
}
interface IFacilitiesData {
  items: BffFacility[];
  reqStatus?: RequestStatus;
}
export default function LocationSearch(props: Readonly<ILocationSearch>) {
  const {
    locations,
    handleClose,
    preSelectedLocation
  } = props;
  const {
    facilityBrand
  } = useFacilityContext();
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const url = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
  const {
    featureFlags
  } = useAppStore();
  const segmentType = useSchedulingStore().returningGuest ? 1 : 0;
  const {
    setSelectedLocation,
    setLocationUpdated,
    selectedTreatmentCode
  } = useSchedulingStore(state => state);
  const [heartedLocation, setHeartedLocation] = useState<string | null>(null);
  const inputProps = {
    'data-test-id': 'locations_search_input_box',
    'aria-label': 'Search locations'
  };
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [facilitiesData, setFacilitiesData] = useState<IFacilitiesData>({
    items: []
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useEffect(() => {
    const savedHeartedLocation = localStorage.getItem(HEART_LOCATION_STORAGE_KEY);
    if (savedHeartedLocation) {
      setHeartedLocation(JSON.parse(savedHeartedLocation));
    }
    if (preSelectedLocation) {
      setSelectedLocation(preSelectedLocation);
    }
  }, [preSelectedLocation, setSelectedLocation]);

  // Shared functions
  const getSearchBox = () => {
    const placeholder = featureFlags.useFacilityBeffeDataSource ? 'Search for a location' : 'Search by name or zip code';
    const endAdornment = featureFlags.useFacilityBeffeDataSource ? <IconButton data-test-id="search-button" type="submit" disabled={searchTerm.length <= 1 || isLoading} onClick={e => {
      e.preventDefault();
      void fetchBffFacilityData();
    }}>
				<TAGSvgIcon icon={'SearchFunc'} size={28} />
			</IconButton> : null;
    return <OutlinedInput value={searchTerm} onChange={e => {
      setSearchTerm(e.target.value);
    }} sx={{
      width: '100%'
    }} inputProps={inputProps} placeholder={placeholder} onKeyDown={handleKeyPress} endAdornment={endAdornment} data-sentry-element="OutlinedInput" data-sentry-component="getSearchBox" data-sentry-source-file="LocationSearch.tsx" />;
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void locationSearchClickEvent();
      void fetchBffFacilityData();
      setIsLoading(true);
    }
  };
  const toggleHeart = (locationId: string) => {
    setHeartedLocation(prevHeartedLocation => {
      const updatedHeartedLocation = prevHeartedLocation === locationId ? null : locationId;
      localStorage.setItem(HEART_LOCATION_STORAGE_KEY, JSON.stringify(updatedHeartedLocation));
      return updatedHeartedLocation;
    });
  };

  // Location change for rest api
  const handleLocationChange = (location: CenterLocation) => {
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'select_a_location',
      facilityId: location.id,
      facilityName: location.display_name,
      segmentType: segmentType
    };
    pushToDataLayer({
      ...dataForDataLayer,
      ...(selectedTreatmentCode && {
        reasonCode: treatmentCodeToReasonCode[selectedTreatmentCode]
      })
    });
    setSelectedLocation(location);
    setLocationUpdated(true);
    handleClose();
  };

  // Beffe Location Search Functions
  const fetchBffFacilityData = async () => {
    const queryString = searchTerm.trim();
    if (!queryString || queryString === '') {
      return clearAndRevertToSelectedFacility();
    }
    let facilities;
    if (queryString) {
      setIsLoading(true);
      const facilitiesListByAddress = await getBffFacilitiesByAddress(facilityBrand.toString() as BffBrandType, queryString, url, featureFlags.fetchAllLocationsOnEmptyResults, appName, appVersion);
      facilities = facilitiesListByAddress?.facilitiesByAddress;
    }
    updateDataFromLocationSearch(facilities as BffFacility[] || []);
    if (facilities) {
      setIsLoading(false);
    }
  };
  const clearAndRevertToSelectedFacility = () => {
    locationSearchClickEvent();
    setSearchTerm('');
  };
  const updateState = useCallback((newState: Partial<IFacilitiesData>) => {
    setFacilitiesData({
      ...facilitiesData,
      ...newState
    });
  }, [setFacilitiesData, facilitiesData]);
  const updateDataFromLocationSearch = (facilitiesByAddress: BffFacility[]) => {
    updateState({
      items: facilitiesByAddress,
      reqStatus: RequestStatus.Success
    });
  };
  const getSelectedLocationById = (locationId: string) => {
    return locations.find(location => location.id === locationId);
  };
  const handleBeffeLocationChange = (location: BffFacility) => {
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'select_a_location',
      facilityId: location.id,
      facilityName: location.name,
      segmentType: segmentType
    };
    pushToDataLayer({
      ...dataForDataLayer,
      ...(selectedTreatmentCode && {
        reasonCode: treatmentCodeToReasonCode[selectedTreatmentCode]
      })
    });
    const selectedLocation = getSelectedLocationById(location.id);
    setSelectedLocation(selectedLocation as CenterLocation);
    setLocationUpdated(true);
    handleClose();
  };
  const locationSearchClickEvent = () => {
    const eventConfig = {
      event: 'location_search_click'
    };
    dataLayerConfig(eventConfig);
  };
  return <>
			<Box sx={dialogStyles.searchBox} data-test-id="search-box" data-sentry-element="Box" data-sentry-source-file="LocationSearch.tsx">
				{getSearchBox()}
			</Box>
			{featureFlags.useFacilityBeffeDataSource ? <BffLocationsList heartedLocation={heartedLocation || ''} facilitiesData={facilitiesData} isLoading={isLoading} toggleHeart={toggleHeart} handleBeffeLocationChange={handleBeffeLocationChange} /> : <RestApiLocationsList locations={locations} heartedLocation={heartedLocation || ''} searchTerm={searchTerm} toggleHeart={toggleHeart} handleLocationChange={handleLocationChange} />}
		</>;
}